import { CommonDialogs } from "@mcleod/common";
import { BlurEvent, Button, DataSource, DataSourceMode, EditRowDecorator, LookupModelSelectionEvent, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutTariffRegionLocationSlideOut } from "./autogen/AutogenLayoutTariffRegionLocationSlideOut";
import { Api, ModelRow, StringUtil } from "@mcleod/core";

export class TariffRegionLocationSlideOut extends AutogenLayoutTariffRegionLocationSlideOut {

    addLocationSlideout(regUID: any, sourceTariffRegionLoc: DataSource) {
        new EditRowDecorator({
            title: "Add Tariff Location",
            layout: this,
            fillVerticalSpace: true,
            width: '50%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffLocation(true, regUID, sourceTariffRegionLoc),
            layoutLoadListeners: async () => {
                this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await this.mainDataSource.createBlankRow()]);
                this.activeRow.set("reg_uid", regUID);
                this.activeRow.set("location_type", "REV");
                this.activeRow.set("effective_date", new Date());
            },
            doAfterSlideIn: () => {
                this.textboxLocationId.enabled = true;
            },
            onClose: async (cancelled: boolean) => {
                if (cancelled && this.mainDataSource.hasChanged()) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                this.slideOut();
                sourceTariffRegionLoc.search({ reg_uid: regUID });
            },
        });
    }

    updateLocationSlideout(regUID: any, sourceTariffRegionLoc: DataSource, modelRow: ModelRow) {
        new EditRowDecorator({
            title: "Update Tariff Location - " + modelRow.get("location_id"),
            layout: this,
            fillVerticalSpace: true,
            width: '50%',
            data: modelRow,
            dataSource: this.mainDataSource,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffLocation(false, regUID, sourceTariffRegionLoc),
            doAfterSlideIn: () => {
                this.textboxLocationId.enabled = false;
            },
            onClose: async (cancelled: boolean) => {
                if (cancelled && this.mainDataSource.hasChanged()) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                this.slideOut();
                sourceTariffRegionLoc.search({ reg_uid: regUID });
            },
        });
    }

    addUpdateTariffLocation(isAddMode: boolean, regUID: any, sourceTariffRegionLoc: DataSource): Button {
        const caption = isAddMode ? "Add" : "Update";
        const addUpdateButton = new Button({
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            caption: caption,
            color: "primary.reverse",
            rowBreak: false,
            onClick: async () =>  {
                try {
                    addUpdateButton.busy = true;
                    const isDataSourceChanged = this.mainDataSource.hasChanged();
                    if (isDataSourceChanged === false) {
                        CommonDialogs.showError("No changes were made. Please try again or check your input.");
                        return;
                    }
                    const activeRow = this.mainDataSource.activeRow
                    if (!this.checkRequiredField(activeRow.get("location_id"), "Location ID")) return;
                    if (!this.checkRequiredField(activeRow.get("effective_date"), "Effective Date")) return;

                    const uniqueID: string = activeRow.get("id", null);
                    const locationID: string = activeRow.get("location_id", null);

                    const effectiveDate = activeRow.get("effective_date", null);
                    const expirationDate = activeRow.get("expiration_date", null);
                    const activeEffectiveDate = Date.parse(effectiveDate);
                    const activeExpireDate = expirationDate != null ? Date.parse(expirationDate) : null;
                    const response = await Api.search("lme/dispatch/tariff-zip-location-validation", {"reg_uid": regUID, "unique_id": uniqueID, "is_location": true});
                    if (response) {
                        const data = response.data[0].response_data;
                        if (data && data.length > 0) {
                            for (const row of data) {
                                const locationIdFromList = row.location_id;
                                if (locationIdFromList === activeRow.get("location_id")) {
                                    const rowEffectiveDate = Date.parse(row.effective_date);
                                    const rowExpireDate = row.expiration_date != null ? Date.parse(row.expiration_date) : null;
                                    const hasAnyExpiration = expirationDate != null || row.expiration_date != null;
                                    const activeEndsBeforeRowStarts = expirationDate != null && activeExpireDate < rowEffectiveDate;
                                    const rowEndsBeforeActiveStarts = row.expiration_date != null && rowExpireDate < activeEffectiveDate;
                                    const noConflict = activeEndsBeforeRowStarts || rowEndsBeforeActiveStarts;
                                    if (!(hasAnyExpiration && noConflict)) {
                                        CommonDialogs.showError(`Location ID ${locationID} has a conflicting effective row in the table.`);
                                        return;
                                    }
                                }
                            }
                        }
                    }

                    this.postTariffLocation(isAddMode, regUID, sourceTariffRegionLoc);

                } catch (error) {
                    CommonDialogs.showError(error);
                }
                finally {
                    addUpdateButton.busy = false;
                }
            }

        })
        return addUpdateButton;
    }

    private checkRequiredField(data: string, value: string) {
        if (data == null) {
            CommonDialogs.showError(value + " is required.");
            return false;
        }
        return true;
    }

    private async postTariffLocation(isAddMode: boolean, regUID: any, sourceTariffRegionLoc: DataSource) {
        try {
            const response = await this.mainDataSource.post();
            if (response) {
                const message = isAddMode ? "Tariff Location Added" : "Tariff Location Updated";
                Toast.showToast(message);
                await this.delay(2000);
                this.slideOut();
                await sourceTariffRegionLoc.search({ reg_uid: regUID });
            }
        } catch (error) {
            CommonDialogs.showError(error);
        }
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    textboxLocationIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedRow = event.selections[0] as ModelRow;
            this.activeRow.set("location_id", selectedRow.get("id"));
            this.activeRow.set("name", selectedRow.get("name"));
            this.activeRow.set("address1", selectedRow.get("address1"));
            this.activeRow.set("city_name", selectedRow.get("city_name"));
            this.activeRow.set("state", selectedRow.get("state"));
            this.activeRow.set("zip_code", selectedRow.get("zip_code"));
        }
    }

    textboxLocationIdOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const locationId = textbox.getDataValue();
        if (StringUtil.isEmptyString(locationId)) {
            this.activeRow.set("location_id", null);
            this.activeRow.set("name", null);
            this.activeRow.set("address1", null);
            this.activeRow.set("city_name", null);
            this.activeRow.set("state", null);
            this.activeRow.set("zip_code", null);
        }
    }

    textboxExpirationDateOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const effectiveDate = this.activeRow.get("effective_date");
        const expireDate = textbox.getDataValue();
        if (effectiveDate != null && expireDate != null && Date.parse(expireDate) < Date.parse(effectiveDate)) {
            textbox.text = "";
            CommonDialogs.showError("Expire date cannot be before effective date.");
        }
    }
}
